import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatEmisorVigenciaDate } from "@fiscalo/shared-utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, X } from "lucide-react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboardMetrics } from "@/hooks/useDashboard";
import { useDashboardView } from "@/hooks/useDashboardView";
import { useOnboardingPending } from "@/hooks/useOnboardingPending";
import {
  invoiceCreationBlockedEmptyExplanationKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { tenantKeys } from "@/lib/queryKeys";

import { finalizeDgiProd, getTenantCurrent } from "@/services/authService";
import { OnboardingCompleteCelebrationModal } from "@/pages/dashboard/OnboardingCompleteCelebrationModal";
import { DashboardOnboardingCard } from "@/pages/dashboard/DashboardOnboardingCard";
import { DashboardActivateFiscaloCard } from "@/pages/dashboard/DashboardActivateFiscaloCard";
import { DashboardFiscalOperationStatus } from "@/pages/dashboard/DashboardFiscalOperationStatus";
import { DashboardFiscalHealthScore } from "@/pages/dashboard/DashboardFiscalHealthScore";
import { DashboardHeader } from "@/pages/dashboard/DashboardHeader";
import { DashboardHeaderSummary } from "@/pages/dashboard/DashboardHeaderSummary";
import { DashboardPreparedByFiscalo } from "@/pages/dashboard/DashboardPreparedByFiscalo";
import { DashboardRecurringInvoicesCard } from "@/pages/dashboard/DashboardRecurringInvoicesCard";
import { DashboardDraftInvoicesCard } from "@/pages/dashboard/DashboardDraftInvoicesCard";
import { DashboardWelcomeEmptyState } from "@/pages/dashboard/DashboardWelcomeEmptyState";
import { PendingRecibidaAckBanner } from "@/pages/dashboard/PendingRecibidaAckBanner";
import { SubscriptionPastDueBanner } from "@/pages/dashboard/SubscriptionPastDueBanner";
import { DashboardFinancialSummary } from "@/pages/dashboard/DashboardFinancialSummary";
import { DashboardMonthSummary } from "@/pages/dashboard/DashboardMonthSummary";
import { DashboardNextObligation } from "@/pages/dashboard/DashboardNextObligation";
import { DashboardRecentActivity } from "@/pages/dashboard/DashboardRecentActivity";
import { DashboardTopSuppliersCard } from "@/pages/dashboard/DashboardTopSuppliersCard";
import type { DashboardResponse } from "@fiscalo/shared-types";

function isDashboardEmpty(data: DashboardResponse): boolean {
  const { accepted, rejected, pending } = data.metrics;
  return accepted + rejected + pending === 0;
}

function isStringMap(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

const ALL_AUTO_FLOW_STEPS = [
  "portal_user",
  "company_info",
  "cae_download",
  "proveedor_software",
] as const;

function celebrationDismissedKey(tenantId: string | null | undefined): string {
  return `fiscalo:onboarding-complete-celebrated:${tenantId ?? "anonymous"}`;
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const showPaidFinancialDashboard = !isFreePlan(tenant?.planId);
  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const emptyBlockedExplanationKey =
    invoiceCreationBlockedEmptyExplanationKey(invoicingBlockedReason);
  // While the user still has onboarding actions to take (cert, CFE) or the
  // DGI auto-flow is mid-chain, the dashboard's normal panels would just
  // show empty / "configurando" cards everywhere. Hide them so the
  // onboarding hero is the sole focus until the account is actually
  // operational. Accountants are an exception: they don't own the tenant
  // and shouldn't be staring at a blank page while the owner finishes setup
  // — show them whatever panels we have.
  const { hasPending: onboardingPendingRaw } = useOnboardingPending();
  // Only mute the pending-onboarding gate for a "guest" accountant (invited
  // into someone else's tenant — membership role 'accountant'). An accountant
  // who created/owns this tenant on a client's behalf is acting as owner
  // here and needs to see the onboarding nudges to finish DGI setup.
  const onboardingPending =
    user?.role === "accountant" ? false : onboardingPendingRaw;

  // Layout preference. Everyone defaults to the simple (mobile-mirroring)
  // view; `isDefaulted` stays true until the user actually picks one, which
  // is what gates the one-time hint below.
  const { view } = useDashboardView();

  const {
    data,
    isLoading: metricsLoading,
    isError: metricsError,
    dataUpdatedAt,
  } = useDashboardMetrics();

  useEffect(() => {
    if (metricsError) {
      toast.error(t("dashboard.errorLoad"));
    }
  }, [metricsError, t]);

  // ── DGI prod promotion + celebration modal ──────────────────────────
  // Conditions for "we should promote and celebrate":
  //   • all 5 auto-flow steps stamped (portal_user → proveedor_software)
  //   • certificate uploaded (hasDgiCredentials)
  //   • currently still in test (ePrueba) on the tenant
  //   • the user hasn't already seen the celebration on this device
  // The flip is server-side gated on the same conditions — this effect is
  // just the trigger and the UX surface.
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  useEffect(() => {
    if (!tenant) return;
    const progress = isStringMap(tenant.dgiAutoOnboardingProgress)
      ? tenant.dgiAutoOnboardingProgress
      : null;
    const autoFlowComplete =
      Boolean(progress) &&
      ALL_AUTO_FLOW_STEPS.every((k) => Boolean(progress?.[k]));
    const certReady = Boolean(tenant.hasDgiCredentials);
    const stillTest = tenant.dgiEnvironment === "test";
    if (!autoFlowComplete || !certReady || !stillTest) return;
    const dismissKey = celebrationDismissedKey(tenant.id);
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(dismissKey) === "1") return;
    } catch {
      // Storage unavailable — bail rather than risk a per-render loop.
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await finalizeDgiProd();
        if (cancelled) return;
        if (res.dgiEnv === "prod") {
          try {
            localStorage.setItem(dismissKey, "1");
          } catch {
            /* ignore */
          }
          setCelebrationOpen(true);
          await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        }
      } catch {
        // Backend rejected (cert removed mid-flow, race, etc.) — no UX surface
        // needed; the user will see the test-mode banner stay until conditions
        // are met for real, and the next dashboard render will retry.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenant, queryClient]);

  // Lighter-touch sibling of the celebration modal: when the user is actively
  // looking at the dashboard the exact moment the chain finishes, fire a
  // toast. We compare against the previous render's snapshot so this only
  // fires on the false→true transition, not on every subsequent render with
  // a still-complete chain. Pair to the email firing in the webhook (36c77c0);
  // toast handles the "user is right here" case, email handles "user closed
  // the tab".
  const prevAutoFlowCompleteRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!tenant) return;
    const progress = isStringMap(tenant.dgiAutoOnboardingProgress)
      ? tenant.dgiAutoOnboardingProgress
      : null;
    const autoFlowComplete =
      Boolean(progress) &&
      ALL_AUTO_FLOW_STEPS.every((k) => Boolean(progress?.[k]));
    const prev = prevAutoFlowCompleteRef.current;
    prevAutoFlowCompleteRef.current = autoFlowComplete;
    if (prev === false && autoFlowComplete) {
      toast.success(
        t(
          "dashboard.autoFlowCompletedToast",
          "Listo: Fiscalo terminó de configurar DGI por vos.",
        ),
        { duration: 6000, id: `dgi-autoflow-complete:${tenant.id}` },
      );
    }
  }, [tenant, t]);

  if (metricsLoading || !data) {
    return <LoadingPage />;
  }

  const recurringDraftsReadyCount = data.recurringDraftsReadyCount ?? 0;
  const recurringSchedulesActiveCount = data.recurringSchedulesActiveCount ?? 0;
  const recurringPendingDrafts = data.recurringPendingDrafts ?? [];
  const hasIvaEstimate =
    showPaidFinancialDashboard &&
    typeof data.financial.ivaAPagarEstimadoUyu === "number";
  const emptyInvoices = isDashboardEmpty(data);

  const celebrationCompanyName =
    tenant?.legalName?.trim() ||
    tenant?.name?.trim() ||
    t("dashboard.onboardingComplete.companyFallback", "Tu empresa");

  // Brand-new tenants (onboarding done, zero CFEs ever) get a friendly
  // empty-state hero in place of the financial / salud-fiscal /
  // próximos-vencimientos panels. Those assume activity and read as
  // alarms on a tenant where nothing has happened yet. The onboarding
  // card stays so any remaining nudges (cert, brand, etc.) still show.
  const showBrandNewEmptyState = !onboardingPending && emptyInvoices;
  // Delegated handoff: a business owner who chose "my accountant continues"
  // and hasn't finished onboarding. They see the full onboarding checklist
  // (so they can act on owner-doable steps like logo/CFE import) with the
  // accountant-handoff banner on top and the technical steps marked read-only
  // ("Lo hace tu contador") — via DashboardOnboardingCard's `delegated` mode.
  const isDelegatedHandoff =
    tenant?.onboardingMode === "accountant" &&
    tenant?.onboardingCompletedAt == null &&
    tenant?.planId != null &&
    user?.accountType !== "accountant";
  return (
    <div className="fx-content space-y-5">
      {onboardingPending ? null : (
        <DashboardHeader
          dataUpdatedAt={dataUpdatedAt}
          view={view}
        />
      )}

      <DashboardOnboardingCard delegated={isDelegatedHandoff} />

      <DashboardActivateFiscaloCard />

      {onboardingPending ? null : showBrandNewEmptyState ? (
        <DashboardWelcomeEmptyState />
      ) : view === "simple" ? (
        <DashboardSimplePanels
          data={data}
          showPaidFinancialDashboard={showPaidFinancialDashboard}
          invoicingBlocked={invoicingBlocked}
          emptyBlockedExplanationKey={emptyBlockedExplanationKey}
          recurringSchedulesActiveCount={recurringSchedulesActiveCount}
          recurringPendingDrafts={recurringPendingDrafts}
          emptyInvoices={emptyInvoices}
          t={t}
        />
      ) : (
        <DashboardPanels
          data={data}
          showPaidFinancialDashboard={showPaidFinancialDashboard}
          invoicingBlocked={invoicingBlocked}
          emptyBlockedExplanationKey={emptyBlockedExplanationKey}
          recurringDraftsReadyCount={recurringDraftsReadyCount}
          recurringSchedulesActiveCount={recurringSchedulesActiveCount}
          recurringPendingDrafts={recurringPendingDrafts}
          hasIvaEstimate={hasIvaEstimate}
          emptyInvoices={emptyInvoices}
          t={t}
        />
      )}

      <OnboardingCompleteCelebrationModal
        open={celebrationOpen}
        onOpenChange={setCelebrationOpen}
        companyName={celebrationCompanyName}
        onCreateFirstInvoice={() => {
          setCelebrationOpen(false);
          navigate("/invoices/new");
        }}
      />
    </div>
  );
}

interface DashboardBannersProps {
  data: DashboardResponse;
  invoicingBlocked: boolean;
  emptyBlockedExplanationKey: ReturnType<
    typeof invoiceCreationBlockedEmptyExplanationKey
  >;
  emptyInvoices: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Actionable banners + the zero-invoices prompt. These are identical in both
 * views — they're calls to action, not the density users complained about.
 */
function DashboardBanners({
  data,
  invoicingBlocked,
  emptyBlockedExplanationKey,
  emptyInvoices,
  t,
}: DashboardBannersProps): JSX.Element {
  return (
    <>
      <SubscriptionPastDueBanner alerts={data.alerts} />

      <PendingRecibidaAckBanner />

      {data.dgiEnvironment === "test" ? (
        <div
          className="flex items-start gap-3 rounded-[var(--fx-r-md)] border border-[var(--fx-ink-200)] bg-[rgba(45,97,73,0.06)] px-4 py-3 text-sm text-[var(--fx-accent-700)] dark:border-[rgba(111,165,133,0.30)] dark:bg-[rgba(111,165,133,0.10)] dark:text-[var(--fx-accent-300)]"
          role="status"
        >
          <Info className="h-5 w-5 shrink-0 text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]" />
          {/* DGI states the go-live date in its authorization mail, and we
              parse it now. Saying "la homologación está en proceso" to a tenant
              whose homologación is finished points at a step they already
              completed; the date answers the question they actually have. The
              generic copy stays for anyone DGI has not dated yet. */}
          <span>
            {formatEmisorVigenciaDate(data.emisorVigenciaDate)
              ? t("dashboard.testModeBanner.messageWithDate", {
                  date: formatEmisorVigenciaDate(data.emisorVigenciaDate),
                })
              : t("dashboard.testModeBanner.message")}
          </span>
        </div>
      ) : null}

      {emptyInvoices ? (
        <div className="fx-panel">
          <div className="px-6 py-8 text-center">
            <p className="mb-3 text-sm" style={{ color: "var(--fx-ink-500)" }}>
              {invoicingBlocked && emptyBlockedExplanationKey
                ? t(emptyBlockedExplanationKey)
                : t("dashboard.empty.explanation")}
            </p>
            {invoicingBlocked ? (
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="fx-btn fx-btn-accent"
              >
                {t("dashboard.empty.cta")}
              </button>
            ) : (
              <Link to="/invoices/new" className="fx-btn fx-btn-accent">
                {t("dashboard.empty.cta")}
              </Link>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

interface DashboardSimplePanelsProps {
  data: DashboardResponse;
  showPaidFinancialDashboard: boolean;
  invoicingBlocked: boolean;
  emptyBlockedExplanationKey: ReturnType<
    typeof invoiceCreationBlockedEmptyExplanationKey
  >;
  recurringSchedulesActiveCount: number;
  recurringPendingDrafts: DashboardResponse["recurringPendingDrafts"];
  emptyInvoices: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Simple view — mirrors the mobile Inicio screen. Each fact appears exactly
 * once, and the money detail sits behind the month card's disclosure.
 *
 * Intentionally omits DashboardPreparedByFiscalo, DashboardFiscalOperationStatus,
 * DashboardHeaderSummary and DashboardTopSuppliersCard: the first three restate
 * IVA / próximo vencimiento / recurring drafts that the cards below already
 * show, and mobile has no top-suppliers equivalent.
 */
function DashboardSimplePanels({
  data,
  showPaidFinancialDashboard,
  invoicingBlocked,
  emptyBlockedExplanationKey,
  recurringSchedulesActiveCount,
  recurringPendingDrafts,
  emptyInvoices,
  t,
}: DashboardSimplePanelsProps): JSX.Element {
  return (
    <>
      <DashboardBanners
        data={data}
        invoicingBlocked={invoicingBlocked}
        emptyBlockedExplanationKey={emptyBlockedExplanationKey}
        emptyInvoices={emptyInvoices}
        t={t}
      />

      {showPaidFinancialDashboard && !emptyInvoices ? (
        <DashboardMonthSummary financial={data.financial} />
      ) : null}

      {showPaidFinancialDashboard ? (
        <DashboardFiscalHealthScore variant="mini" />
      ) : null}

      <DashboardNextObligation
        showPaidFiscalDashboard={showPaidFinancialDashboard}
      />

      <DashboardRecurringInvoicesCard
        schedulesActiveCount={recurringSchedulesActiveCount}
        pendingDrafts={recurringPendingDrafts}
      />

      <DashboardDraftInvoicesCard />

      <DashboardRecentActivity
        lastSubmissions={data.lastSubmissions}
        lastReceivedInvoices={data.lastReceivedInvoices}
      />
    </>
  );
}

interface DashboardPanelsProps {
  data: DashboardResponse;
  showPaidFinancialDashboard: boolean;
  invoicingBlocked: boolean;
  emptyBlockedExplanationKey: ReturnType<
    typeof invoiceCreationBlockedEmptyExplanationKey
  >;
  recurringDraftsReadyCount: number;
  recurringSchedulesActiveCount: number;
  recurringPendingDrafts: DashboardResponse["recurringPendingDrafts"];
  hasIvaEstimate: boolean;
  emptyInvoices: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}

function DashboardPanels({
  data,
  showPaidFinancialDashboard,
  invoicingBlocked,
  emptyBlockedExplanationKey,
  recurringDraftsReadyCount,
  recurringSchedulesActiveCount,
  recurringPendingDrafts,
  hasIvaEstimate,
  emptyInvoices,
  t,
}: DashboardPanelsProps): JSX.Element {
  return (
    <>
      <DashboardBanners
        data={data}
        invoicingBlocked={invoicingBlocked}
        emptyBlockedExplanationKey={emptyBlockedExplanationKey}
        emptyInvoices={emptyInvoices}
        t={t}
      />

      {showPaidFinancialDashboard && !emptyInvoices && (
        <DashboardFinancialSummary
          metrics={data.metrics}
          financial={data.financial}
        />
      )}

      {showPaidFinancialDashboard ? (
        <>
          <DashboardPreparedByFiscalo
            recurringDraftsReadyCount={recurringDraftsReadyCount}
            showPaidFiscalDashboard={showPaidFinancialDashboard}
            hasIvaEstimate={hasIvaEstimate}
          />
          <div className="fx-grid-two items-start">
            <DashboardFiscalOperationStatus
              metrics={data.metrics}
              financial={data.financial}
              alerts={data.alerts}
              dismissedAlertIds={data.dismissedAlertIds ?? []}
              recurringDraftsReadyCount={recurringDraftsReadyCount}
              showPaidFiscalDashboard={showPaidFinancialDashboard}
            />
            <DashboardFiscalHealthScore />
          </div>
        </>
      ) : (
        <>
          <DashboardFiscalOperationStatus
            metrics={data.metrics}
            financial={data.financial}
            alerts={data.alerts}
            dismissedAlertIds={data.dismissedAlertIds ?? []}
            recurringDraftsReadyCount={recurringDraftsReadyCount}
            showPaidFiscalDashboard={showPaidFinancialDashboard}
          />
          <DashboardPreparedByFiscalo
            recurringDraftsReadyCount={recurringDraftsReadyCount}
            showPaidFiscalDashboard={showPaidFinancialDashboard}
            hasIvaEstimate={hasIvaEstimate}
          />
        </>
      )}

      <DashboardHeaderSummary
        metrics={data.metrics}
        financial={data.financial}
        alerts={data.alerts}
        dismissedAlertIds={data.dismissedAlertIds ?? []}
        recurringDraftsReadyCount={recurringDraftsReadyCount}
        showPaidFiscalDashboard={showPaidFinancialDashboard}
      />

      <DashboardRecurringInvoicesCard
        schedulesActiveCount={recurringSchedulesActiveCount}
        pendingDrafts={recurringPendingDrafts}
      />

      <DashboardDraftInvoicesCard />

      <DashboardRecentActivity
        lastSubmissions={data.lastSubmissions}
        lastReceivedInvoices={data.lastReceivedInvoices}
      />

      <DashboardTopSuppliersCard topSuppliers={data.topSuppliersYtd ?? []} />
    </>
  );
}
